/**
 * Dashboard store.
 *
 * Zustand holds only UI state (modal, refresh spinner). Server data —
 * admin profile, applications, certifications, codes, notifications —
 * lives in the React Query cache; the selector hooks below read from it
 * and the former data "mutators" invalidate queries instead of patching
 * local arrays. Socket wiring lives in DashboardWrapper.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import { type QueryKey } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import socketService from '@/services/socketService';
import { useToken } from '@/hooks/useToken';
import { useAdminMe, ADMIN_KEYS } from '@/hooks/api/useAdmin';
import { useDocuments, DOCUMENTS_KEYS } from '@/hooks/api/useDocuments';
import {
  useCertifications,
  CERTIFICATIONS_KEYS,
} from '@/hooks/api/useCertifications';
import { useCodeRequests, CODES_KEYS } from '@/hooks/api/useCodes';
import {
  useNotifications,
  NOTIFICATIONS_KEYS,
} from '@/hooks/api/useNotifications';
import type {
  DocumentModel,
  CertificationModel,
  CodeRequestModel,
  NotificationModel,
  UserAdminModel,
} from '@/api/types';

// Server responses arrive oldest-first; the UI renders newest-first
const reversed = <T>(list?: T[]): T[] => (list ? [...list].reverse() : []);

const invalidate = (queryKey: QueryKey) =>
  queryClient.invalidateQueries({ queryKey });

const REFRESH_KEYS: QueryKey[] = [
  DOCUMENTS_KEYS.all,
  CERTIFICATIONS_KEYS.all,
  CODES_KEYS.all,
  NOTIFICATIONS_KEYS.all,
];

interface DashboardModal {
  open: boolean;
  index: number;
  view: unknown;
}

export interface DashboardStore {
  // UI state
  modal: DashboardModal;
  refreshing: boolean;

  // Modal actions
  openModal: (index: number, view: unknown) => void;
  changeModalIndex: (index: number) => void;
  resetModal: () => void;

  // Data "mutators" (invalidate the backing query)
  setApplications: () => void;
  addApplication: () => void;
  changeApplicationStatus: () => void;
  setCertifications: () => void;
  addCertification: () => void;
  changeCertificationStatus: () => void;
  setCodes: () => void;
  addCode: () => void;
  changeCodeStatus: () => void;
  setNotifications: () => void;
  addNotification: () => void;
  updateProfile: () => void;

  // Socket emit passthrough
  handleCallToEmit: (id: string, emitRequest: unknown) => void;

  // Manual refresh
  refreshDashboardState: () => Promise<void>;
}

export const useDashboardStore = create<DashboardStore>()(set => ({
  // UI state
  modal: {
    open: false,
    index: 1,
    view: null,
  },
  refreshing: false,

  // Modal actions
  openModal: (index, view) =>
    set({
      modal: {
        open: true,
        index,
        view,
      },
    }),

  changeModalIndex: index =>
    set(state => ({
      modal: {
        ...state.modal,
        index,
      },
    })),

  resetModal: () =>
    set({
      modal: {
        open: false,
        index: 1,
        view: null,
      },
    }),

  // Data "mutators" — kept for call-site compatibility; each one now
  // invalidates the backing query so React Query refetches fresh data
  setApplications: () => invalidate(DOCUMENTS_KEYS.all),
  addApplication: () => invalidate(DOCUMENTS_KEYS.all),
  changeApplicationStatus: () => invalidate(DOCUMENTS_KEYS.all),
  setCertifications: () => invalidate(CERTIFICATIONS_KEYS.all),
  addCertification: () => invalidate(CERTIFICATIONS_KEYS.all),
  changeCertificationStatus: () => invalidate(CERTIFICATIONS_KEYS.all),
  setCodes: () => invalidate(CODES_KEYS.all),
  addCode: () => invalidate(CODES_KEYS.all),
  changeCodeStatus: () => invalidate(CODES_KEYS.all),
  setNotifications: () => invalidate(NOTIFICATIONS_KEYS.all),
  addNotification: () => invalidate(NOTIFICATIONS_KEYS.all),
  updateProfile: () => invalidate(ADMIN_KEYS.me),

  // Socket emit passthrough
  handleCallToEmit: (id, emitRequest) => {
    socketService.emit(id, emitRequest);
  },

  // Manual refresh: refetch all dashboard collections
  refreshDashboardState: async () => {
    set({ refreshing: true });
    try {
      await Promise.all(
        REFRESH_KEYS.map(queryKey => queryClient.refetchQueries({ queryKey })),
      );
    } finally {
      set({ refreshing: false });
    }
  },
}));

// ===== DATA SELECTORS (React Query backed) =====

export const useDashboardModal = () => useDashboardStore(state => state.modal);

export const useDashboardApplications = (): DocumentModel[] => {
  const { data } = useDocuments();
  return useMemo(() => reversed(data?.data), [data]);
};

export const useDashboardNotifications = (): NotificationModel[] => {
  const { data } = useNotifications();
  // /notify returns a bare array (no { data } envelope).
  return useMemo(() => reversed(data), [data]);
};

export const useDashboardCertifications = (): CertificationModel[] => {
  const { data } = useCertifications();
  return useMemo(() => reversed(data?.data), [data]);
};

export const useDashboardCodes = (): CodeRequestModel[] => {
  const { data } = useCodeRequests();
  return useMemo(() => reversed(data?.data), [data]);
};

interface DashboardUser {
  id: string;
  isVerified: boolean;
  token: string | undefined;
  details: UserAdminModel;
}

export const useDashboardUser = (): DashboardUser | null => {
  const token = useToken();
  const { data } = useAdminMe();
  return useMemo(() => {
    const details = data?.users;
    if (!details) return null;
    return {
      id: details._id,
      isVerified: false,
      token,
      details,
    };
  }, [data, token]);
};

export const useDashboardRole = () => {
  const { data } = useAdminMe();
  return data?.users?.role ?? false;
};

export const useDashboardRefreshing = () =>
  useDashboardStore(state => state.refreshing);

// ===== ACTION HOOKS =====

export const useOpenModal = () => useDashboardStore(state => state.openModal);
export const useChangeModalIndex = () =>
  useDashboardStore(state => state.changeModalIndex);
export const useResetModal = () => useDashboardStore(state => state.resetModal);
export const useHandleCallToEmit = () =>
  useDashboardStore(state => state.handleCallToEmit);
export const useRefreshDashboardState = () =>
  useDashboardStore(state => state.refreshDashboardState);

// ===== COMPOSED COMPAT HOOKS =====

export const useDashboardContext = () => {
  const modal = useDashboardModal();
  const applications = useDashboardApplications();
  const notifications = useDashboardNotifications();
  const certifications = useDashboardCertifications();
  const codes = useDashboardCodes();
  const user = useDashboardUser();
  const role = useDashboardRole();

  return {
    modal,
    applications,
    notifications,
    certifications,
    codes,
    user,
    role,
  };
};

export const useDashboardRefresh = () => {
  const refreshing = useDashboardRefreshing();
  const refreshDashboardState = useRefreshDashboardState();

  return {
    refreshing,
    handleRefreshDashboardState: refreshDashboardState,
  };
};

export const useDashboardSocketDispatch = () => useHandleCallToEmit();
